<div class="legal-page">
  <article class="legal-document">
    <header>
      <h1>Privacy Policy</h1>
      <p class="effective">Effective {{ effectiveDate }}</p>
    </header>

    <p class="intro">
      This Privacy Policy explains how <strong>BusinessHub LLC</strong> ("BusinessHub",
      "we", "us") collects, uses, stores, and shares information when you use the BusinessHub
      financial planning and analysis platform. It applies to the platform and to the
      third-party integrations you choose to connect to it.
    </p>

    <h2>1. Information We Collect</h2>

    <h3>Account information</h3>
    <p>
      When an account is created for you we collect your name and email address, and the role
      and application memberships that determine what you can access. Authentication is
      handled by Microsoft Azure AD B2C; we do not store your password.
    </p>

    <h3>Customer data</h3>
    <p>
      We process the business data you upload, enter, or connect — datasets, metrics,
      filters, dashboards, tables, and files. This data belongs to you. We access it to
      provide the platform and do not use it to build profiles or for advertising.
    </p>

    <h3>Usage and diagnostic data</h3>
    <p>
      We record application activity such as sign-in events, configuration changes, and audit
      log entries, along with technical information like error reports and timestamps. This
      is used to operate the service, investigate problems, and maintain security.
    </p>

    <h2>2. QuickBooks Online Integration</h2>
    <p>
      If you connect QuickBooks Online, the following applies specifically to that
      integration.
    </p>

    <h3>What we access</h3>
    <p>
      We access only the QuickBooks data required for the features you use. Today that is
      your company profile information (company name, legal name, and country) and your chart
      of accounts, including account names, types, classifications, account numbers, balances,
      currency, and hierarchy. We read this data; we do not create, modify, or delete records
      in your QuickBooks company.
    </p>

    <h3>Authorisation and tokens</h3>
    <p>
      Connecting QuickBooks uses Intuit's OAuth 2.0 flow. You sign in to Intuit directly —
      your Intuit credentials are never seen by, sent to, or stored by BusinessHub. The
      resulting access and refresh tokens are held encrypted on our servers, are never
      transmitted to your browser, and are used solely to make requests to Intuit on your
      behalf.
    </p>

    <h3>Where synced data is stored</h3>
    <p>
      Data retrieved from QuickBooks is written to the destination database you nominate for
      that connection. Where that database is one you own and operate, the data resides in
      your own infrastructure under your control.
    </p>

    <h3>Disconnecting</h3>
    <p>
      You can disconnect QuickBooks at any time from the Integrations section of the
      administration area. Disconnecting revokes our authorisation with Intuit and deletes the
      stored tokens, ending our ability to access your QuickBooks company. Data already synced
      into your destination database remains there and under your control until you remove it.
      You can also revoke access from within QuickBooks itself.
    </p>

    <h2>3. How We Use Information</h2>
    <ul>
      <li>To provide, maintain, and secure the platform and its features.</li>
      <li>To authenticate users and enforce the access permissions you configure.</li>
      <li>To retrieve data from the integrations you have authorised.</li>
      <li>To diagnose faults, monitor reliability, and prevent abuse.</li>
      <li>To communicate with you about service changes, security matters, and support.</li>
      <li>To comply with legal obligations.</li>
    </ul>
    <p>
      <strong>We do not sell your personal information or your business data, and we do not
      use it for advertising.</strong>
    </p>

    <h2>4. How We Share Information</h2>
    <p>We share information only in these circumstances:</p>
    <ul>
      <li>
        <strong>Service providers.</strong> We use infrastructure and service vendors — such
        as cloud hosting, database, and identity providers — who process data on our behalf
        under contractual obligations to protect it and to use it only as we instruct.
      </li>
      <li>
        <strong>Integrations you authorise.</strong> When you connect a third-party service,
        data flows between that service and the platform as you have directed.
      </li>
      <li>
        <strong>Within your organisation.</strong> Other users of your application see data
        according to the roles and security rules your administrators configure.
      </li>
      <li>
        <strong>Legal requirements.</strong> Where we are required to by law, or to protect
        the rights, safety, or property of BusinessHub, our customers, or the public.
      </li>
      <li>
        <strong>Business transfers.</strong> In connection with a merger, acquisition, or sale
        of assets, subject to the protections of this policy.
      </li>
    </ul>

    <h2>5. Security</h2>
    <p>
      We use industry-standard measures to protect information, including encryption in
      transit, encryption of stored third-party credentials, role-based access controls, and
      per-application data separation so that one customer's data is not accessible to
      another. Access to production systems is limited to personnel who need it. No method of
      transmission or storage is completely secure, and we cannot guarantee absolute security.
    </p>

    <h2>6. Data Retention and Deletion</h2>
    <p>
      We retain account and customer data for as long as your account is active or as needed
      to provide the platform. When an account is closed, we delete or de-identify data within
      a reasonable period, except where we are required to retain it to comply with legal
      obligations, resolve disputes, or enforce agreements. Integration tokens are deleted
      immediately when an integration is disconnected. You may request deletion of your data
      at any time using the contact details below.
    </p>

    <h2>7. Your Rights</h2>
    <p>
      Depending on where you live, you may have the right to access, correct, export, or
      delete personal information we hold about you, to object to or restrict certain
      processing, and to withdraw consent. Where we process data on behalf of a customer
      organisation, we will direct requests to that organisation as the controller of the
      data. To exercise a right, contact us using the details below; we will respond within
      the period required by applicable law.
    </p>

    <h2>8. International Transfers</h2>
    <p>
      We operate in the United States and may process and store information there. If you
      access the platform from outside the United States, you understand that your information
      may be transferred to and processed in a country whose data protection laws differ from
      those of your own, and we take steps to ensure it remains protected as described here.
    </p>

    <h2>9. Cookies and Similar Technologies</h2>
    <p>
      We use cookies and browser storage that are necessary to operate the platform, such as
      maintaining your signed-in session and remembering which application you selected. We do
      not use advertising or cross-site tracking cookies.
    </p>

    <h2>10. Changes to this Policy</h2>
    <p>
      We may update this policy from time to time. If we make material changes we will provide
      reasonable notice before they take effect and update the effective date above.
    </p>

    <h2>11. Contact Us</h2>
    <p>For privacy questions or requests, contact:</p>
    <address>
      <strong>BusinessHub LLC</strong><br />
      Chicago, IL<br />
      <a href="mailto:info&#64;businesshubapp.com">info&#64;businesshubapp.com</a><br />
      +1 (708) 220-9276
    </address>

    <footer>
      <p>Last updated {{ effectiveDate }}.</p>
      <p><a routerLink="/terms">End User License Agreement</a></p>
    </footer>
  </article>
</div>
